/**
 * In-app browser detection (PLAN.md §11.1).
 *
 * Probably the single largest cause of the 40% drop-off. A link opened from
 * Facebook, Instagram, Line or WeChat runs in an embedded webview, and those
 * webviews routinely deny camera access — which is the entire content of step
 * 2. The user gets a permission prompt that never resolves, or no prompt at
 * all, and leaves.
 *
 * Detection is user-agent sniffing, which is unreliable by nature, so it is
 * only ever used to *offer* help. Nobody is blocked on the strength of it: a
 * false positive shows an unnecessary tip, a false negative shows nothing at
 * all, and both are recoverable. Feature-detecting the camera is not an option
 * — `getUserMedia` exists in these webviews and only fails once the user is
 * already committed.
 */

/** Ordered so the more specific apps match before the generic wrappers. */
const IN_APP_PATTERNS: Array<[name: string, pattern: RegExp]> = [
  ['Instagram', /Instagram/i],
  ['Facebook', /FBAN|FBAV|FB_IAB/i],
  ['Line', /\bLine\//i],
  ['WeChat', /MicroMessenger/i],
  ['TikTok', /BytedanceWebview|musical_ly/i],
  ['Messenger', /Messenger/i],
  ['Twitter', /Twitter/i],
  ['LinkedIn', /LinkedInApp/i],
  ['Snapchat', /Snapchat/i],
  ['KakaoTalk', /KAKAOTALK/i],
]

export interface WebviewInfo {
  isInApp: boolean
  /** The app name when we recognise it, for copy that names the thing. */
  app?: string
  /** iOS webviews cannot hand off to Safari programmatically; Android often can. */
  platform: 'ios' | 'android' | 'other'
}

export function detectWebview(userAgent: string = navigator.userAgent): WebviewInfo {
  const platform = /iPhone|iPad|iPod/i.test(userAgent)
    ? 'ios'
    : /Android/i.test(userAgent)
      ? 'android'
      : 'other'

  for (const [app, pattern] of IN_APP_PATTERNS) {
    if (pattern.test(userAgent)) return { isInApp: true, app, platform }
  }
  return { isInApp: false, platform }
}

/**
 * An `intent://` URL that asks Android to reopen the page in a real browser.
 *
 * Android only. iOS has no equivalent — the honest answer there is to tell the
 * user to tap "Open in Safari" themselves, which is why the copy differs.
 */
export function androidEscapeUrl(url: string): string {
  const stripped = url.replace(/^https?:\/\//, '')
  return `intent://${stripped}#Intent;scheme=https;action=android.intent.action.VIEW;end`
}
